import { Drawer, notification } from "antd";
import cuid from "cuid";
import { find, get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineAddTask, MdOutlineEmail } from "react-icons/md";
import Avatar from "shared/components/Avatar";
import CallButton from "shared/components/CallButton";
import { useEmailComposer } from "shared/components/EmailComposer/context";
import Select from "shared/components/Select";
import Tabs from "shared/components/Tab";
import Tooltip from "shared/components/Tooltip";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { UPDATE_CONTACT } from "utils/api/graphql/mutations/contacts";
import { CONTACT } from "utils/api/graphql/queries/contacts";
import {
  CLIENT_PHONE_PREFIXES,
  CONTACT_POINT_SYSTEM,
  USER_TYPES,
} from "utils/constants";
import {
  buildPhoneTelecomsUpdate,
  getPhoneFromTelecoms,
  resolveClientPhoneInput,
} from "utils/helpers/phone";
import CreateTaskModal from "./Modals/CreateTaskModal";
import useProjectMergedActivities from "./hooks/useProjectMergedActivities";
import Activities from "./Tabs/Activities";
import Information from "./Tabs/Information";
import Notes from "./Tabs/Notes";
import Projects from "./Tabs/Projects";

const View = ({
  contact,
  active,
  setActive,
  project,
  projects,
  drawer,
  onClose,
  onAssign,
  isAssigning,
  qualifiers,
  currentQualifier,
  addQualification,
  tracers,
  matchStatusesWithProcess,
}) => {
  const { user } = useContext(StoreContext);
  const { openComposer } = useEmailComposer();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const DRAWER = t("DRAWER", { returnObjects: true });
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const qualificationsOptions = qualifiers.map(({ name, description, id }) => ({
    value: id,
    label: name,
    popover: description,
  }));

  const [updateContact] = useMutation(UPDATE_CONTACT);
  const [drawerWidth, setDrawerWidth] = useState("520px");
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [activitiesCount, setActivitiesCount] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(
    get(currentQualifier, "qualifier.name") || "-",
  );

  const isExternalAdvisor =
    get(user, "meta.type") === USER_TYPES.EXTERNAL_ADVISOR.type;

  const primaryPhoneForActivities = useMemo(() => {
    const telecoms = get(contact, "telecoms", []);
    return (
      getPhoneFromTelecoms(telecoms) ||
      getPhoneFromTelecoms(telecoms, CONTACT_POINT_SYSTEM.LANDLINE) ||
      null
    );
  }, [contact]);

  const skipActivitiesQueries = !drawer || isExternalAdvisor;

  const activitiesQuery = useProjectMergedActivities({
    project,
    contactId: contact?.id,
    phoneNumber: primaryPhoneForActivities,
    skip: skipActivitiesQueries,
  });

  useEffect(() => {
    if (!drawer || skipActivitiesQueries) {
      if (!drawer) {
        setActivitiesCount(null);
      }
      return;
    }
    if (activitiesQuery.isPageLoading) {
      return;
    }
    setActivitiesCount(activitiesQuery.activitiesTotalCount);
  }, [
    activitiesQuery.activitiesTotalCount,
    activitiesQuery.isPageLoading,
    drawer,
    skipActivitiesQueries,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth <= 768) {
        setDrawerWidth("95%");
      } else if (windowWidth <= 1024) {
        setDrawerWidth("70%");
      } else {
        setDrawerWidth("520px");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setSelectedStatus(currentQualifier?.qualifier?.name || "-");
  }, [currentQualifier?.qualifier?.name]);

  const handleUpdateEmail = async (newEmail) => {
    const existingEmailTelecom = find(contact.telecoms, {
      system: CONTACT_POINT_SYSTEM.EMAIL,
    });

    try {
      const { data: mutationData } = await updateContact({
        variables: {
          where: { id: contact.id },
          data: {
            telecoms: [
              ...omitDeep(contact.telecoms, "__typename").filter(
                (telecom) => telecom.system !== CONTACT_POINT_SYSTEM.EMAIL,
              ),
              {
                id: get(existingEmailTelecom, "id", cuid()),
                system: CONTACT_POINT_SYSTEM.EMAIL,
                value: newEmail,
              },
            ],
          },
        },
        refetchQueries: [
          {
            query: CONTACT,
            variables: { where: { id: contact.id } },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (!get(mutationData, "updateContact.id")) {
        throw new Error("Contact update failed");
      }

      notification.success({
        message: DRAWER.TABS.INFORMATION.UPDATE_EMAIL_SUCCESS,
        closeIcon: null,
      });
    } catch {
      notification.error({
        message: DRAWER.TABS.INFORMATION.UPDATE_ERROR,
        closeIcon: null,
      });
      throw new Error("Contact update failed");
    }
  };

  const handleUpdatePhone = async ({ phone, phonePrefix }) => {
    try {
      const resolvedPhone = resolveClientPhoneInput({
        phone,
        phonePrefix,
        prefixOptions: CLIENT_PHONE_PREFIXES,
      });
      const { data: mutationData } = await updateContact({
        variables: {
          where: { id: contact.id },
          data: {
            telecoms: buildPhoneTelecomsUpdate({
              telecoms: omitDeep(contact.telecoms, "__typename"),
              localNumber: resolvedPhone.localNumber,
              prefix: resolvedPhone.prefix || phonePrefix,
              createId: cuid,
            }),
          },
        },
        refetchQueries: [
          {
            query: CONTACT,
            variables: { where: { id: contact.id } },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (!get(mutationData, "updateContact.id")) {
        throw new Error("Contact update failed");
      }

      notification.success({
        message: DRAWER.TABS.INFORMATION.UPDATE_PHONE_SUCCESS,
        closeIcon: null,
      });
    } catch {
      notification.error({
        message: DRAWER.TABS.INFORMATION.UPDATE_ERROR,
        closeIcon: null,
      });
      throw new Error("Contact update failed");
    }
  };

  const handleTabChange = (key) => {
    setActive(key);
  };

  const onCloseDrawer = () => {
    onClose();
    setActive("INFORMATION");
  };

  const getPrimaryPhone = () => {
    const telecoms = get(contact, "telecoms", []);
    return (
      getPhoneFromTelecoms(telecoms) ||
      getPhoneFromTelecoms(telecoms, CONTACT_POINT_SYSTEM.LANDLINE) ||
      null
    );
  };

  const getPrimaryEmail = () => {
    const telecoms = get(contact, "telecoms", []);
    const email = find(telecoms, { system: CONTACT_POINT_SYSTEM.EMAIL });
    if (email && get(email, "value")) return get(email, "value");
    return null;
  };

  const senderEmailDisabled = !!get(
    user,
    "organization.config.emailConfig.disabled",
  );

  const isEmailDisabled = !getPrimaryEmail() || senderEmailDisabled;

  const getEmailTooltip = () => {
    if (senderEmailDisabled) {
      return DRAWER.SEND_EMAIL?.TOOLTIP_NO_SENDER_EMAIL;
    }
    if (!getPrimaryEmail()) {
      return DRAWER.SEND_EMAIL?.TOOLTIP_NO_CONTACT_EMAIL;
    }
    return DRAWER.SEND_EMAIL?.TOOLTIP;
  };

  const handleOpenComposer = () => {
    if (isEmailDisabled) return;
    openComposer({
      contact,
      project,
      advisor: user,
      organization: get(user, "organization"),
    });
  };

  // antd skips `onChange` when the picked value is already selected, so the
  // handler is wired on `onSelect`: qualifying the same way twice in a row is a
  // real event (a second call attempt) and has to be recorded.
  const handleStatusSelect = (qualifierId) => {
    const qualifier = find(qualifiers, { id: qualifierId });
    setSelectedStatus(get(qualifier, "name") || "-");
    addQualification({
      variables: {
        data: {
          id: cuid(),
          qualifier: { id: qualifierId },
          contact: { id: contact.id },
        },
      },
      onCompleted: () => {
        onAssign({ projectId: get(project, "id"), authorId: get(user, "id") });
      },
    });
  };

  const TABS = {
    [true]: [
      {
        key: "INFORMATION",
        children: (
          <Information
            project={project}
            translate={TRANSLATION}
            data={contact}
            onUpdateEmail={handleUpdateEmail}
            onUpdatePhone={handleUpdatePhone}
          />
        ),
        label: DRAWER.TABS.INFORMATION.TITLE,
      },
    ],
    [false]: [
      {
        key: "INFORMATION",
        children: (
          <Information
            project={project}
            translate={TRANSLATION}
            data={contact}
            onUpdateEmail={handleUpdateEmail}
            onUpdatePhone={handleUpdatePhone}
          />
        ),
        label: DRAWER.TABS.INFORMATION.TITLE,
      },
      {
        key: "PROJECTS",
        children: (
          <Projects
            translate={TRANSLATION}
            isAssigning={isAssigning}
            onAssign={onAssign}
            projects={projects}
            tracers={tracers}
          />
        ),
        label: `${DRAWER.TABS.PROJECTS.TITLE} (${projects?.count})`,
      },
      {
        key: "ACTIVITIES",
        children: <Activities {...activitiesQuery} />,
        label:
          activitiesCount == null
            ? DRAWER.TABS.ACTIVITIES.TITLE
            : `${DRAWER.TABS.ACTIVITIES.TITLE} (${activitiesCount})`,
      },
      {
        key: "NOTES",
        children: <Notes projectId={project?.id} />,
        label: DRAWER.TABS.NOTES.TITLE,
      },
    ],
  };

  return (
    <Drawer
      title={<></>}
      placement="right"
      onClose={onCloseDrawer}
      open={drawer}
      className="contact__drawer"
      contentWrapperStyle={{
        width: drawerWidth,
      }}
    >
      <div className="profile--avatar">
        <Avatar
          mode={"text"}
          size={"large"}
          value={`${get(contact, "user.firstname", "")?.substring(0, 1)}${get(
            contact,
            "user.lastname",
            "",
          )?.substring(0, 1)}`}
        />
        <div className="info">
          <span className="name">
            {get(contact, "user.firstname")} {get(contact, "user.lastname")}
          </span>
          <span className="source">{get(project, "tracer.name", " ")}</span>
          {get(contact, "countedCallsCount", 0) > 0 && (
            <Tooltip
              title={DRAWER.CALL_COUNTER_TOOLTIP}
              getPopupContainer={(trigger) =>
                trigger?.closest(".ant-drawer-content") || document.body
              }
            >
              <span className="contact__call-counter">
                {t("DRAWER.CALL_COUNTER", {
                  count: get(contact, "countedCallsCount", 0),
                })}
              </span>
            </Tooltip>
          )}
        </div>
        <Select
          popupMatchSelectWidth={false}
          type="tag"
          value={selectedStatus}
          onSelect={handleStatusSelect}
          options={qualificationsOptions}
        />
      </div>
      <div className="cta-bar">
        <Tooltip
          title={DRAWER.CREATE_TASK?.TOOLTIP}
          getPopupContainer={(trigger) =>
            trigger?.closest(".ant-drawer-content") || document.body
          }
        >
          <div className="cta-bar__btn" onClick={() => setTaskModalOpen(true)}>
            <MdOutlineAddTask />
          </div>
        </Tooltip>
        <CallButton
          phoneNumber={getPrimaryPhone()}
          contactId={contact?.id}
          projectId={project?.id}
          getPopupContainer={(trigger) =>
            trigger?.closest(".ant-drawer-content") || document.body
          }
        />
        <Tooltip
          title={getEmailTooltip()}
          getPopupContainer={(trigger) =>
            trigger?.closest(".ant-drawer-content") || document.body
          }
        >
          <div
            className={`cta-bar__btn${isEmailDisabled ? " cta-bar__btn--disabled" : ""}`}
            onClick={handleOpenComposer}
          >
            <MdOutlineEmail />
          </div>
        </Tooltip>
      </div>
      <Tabs
        destroyInactiveTabPane
        items={get(TABS, isExternalAdvisor, [])}
        activeKey={active}
        onChange={handleTabChange}
      />
      <CreateTaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        contact={contact}
        project={project}
        user={user}
        t={t}
      />
    </Drawer>
  );
};
export default View;
